import {
  useState,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';

import { cn, sanitizeInput, inputValidation } from '../../utils';

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  error?: string;
  helperText?: string;
  variant?: 'default' | 'filled' | 'outlined';
  inputSize?: 'sm' | 'md' | 'lg';
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  loading?: boolean;
  validate?: boolean;
  sanitize?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      label,
      error,
      helperText,
      variant = 'default',
      inputSize = 'md',
      leftIcon,
      rightIcon,
      loading = false,
      validate = false,
      sanitize = true,
      type = 'text',
      id,
      required,
      'aria-describedby': ariaDescribedBy,
      onChange,
      onBlur,
      ...props
    },
    ref
  ) => {
    const [internalError, setInternalError] = useState<string>('');
    const inputRef = useRef<HTMLInputElement>(null);
    const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;
    const errorId = `${inputId}-error`;
    const helperId = `${inputId}-helper`;

    // Combine external ref with internal ref
    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    const currentError = error || internalError;
    const hasError = Boolean(currentError);

    // Build aria-describedby
    const describedByIds = [];
    if (currentError) describedByIds.push(errorId);
    if (helperText) describedByIds.push(helperId);
    if (ariaDescribedBy) describedByIds.push(ariaDescribedBy);
    const finalAriaDescribedBy =
      describedByIds.length > 0 ? describedByIds.join(' ') : undefined;

    const sizeClasses = {
      sm: 'h-8 px-2 text-sm',
      md: 'h-10 px-3 text-sm',
      lg: 'h-12 px-4 text-base',
    };

    const variantClasses = {
      default:
        'border border-gray-300 bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500',
      filled:
        'border-0 bg-gray-100 focus:bg-white focus:ring-2 focus:ring-blue-500',
      outlined: 'border-2 border-gray-300 bg-transparent focus:border-blue-500',
    };

    const baseClasses = cn(
      'w-full rounded-md transition-colors duration-200',
      'placeholder:text-gray-400',
      'disabled:cursor-not-allowed disabled:opacity-50',
      'focus:outline-none',
      sizeClasses[inputSize],
      variantClasses[variant],
      hasError && 'border-red-500 focus:border-red-500 focus:ring-red-500',
      leftIcon && 'pl-10',
      rightIcon && 'pr-10',
      className
    );

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      let value = e.target.value;

      // Sanitize input if enabled
      if (sanitize) {
        value = sanitizeInput.text(value);
        if (value !== e.target.value) {
          e.target.value = value;
        }
      }

      // Validate input if enabled
      if (validate) {
        let validationError = '';

        if (
          type === 'email' &&
          !inputValidation.validateEmail(e.target.value)
        ) {
          validationError = 'Please enter a valid email address';
        } else if (required && !e.target.value.trim()) {
          validationError = 'This field is required';
        }

        if (validationError) {
          setInternalError(validationError);
          // Don't call formUtils.associateError as we handle it internally
        }
      }

      // Clear internal error when user starts typing
      if (internalError) {
        setInternalError('');
        // Don't call formUtils.clearError as we handle it internally
      }

      onChange?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      // setIsFocused(false); // Removed as per edit hint

      // Validate on blur if validation is enabled
      if (validate && e.target.value) {
        let validationError = '';

        if (
          type === 'email' &&
          !inputValidation.validateEmail(e.target.value)
        ) {
          validationError = 'Please enter a valid email address';
        } else if (required && !e.target.value.trim()) {
          validationError = 'This field is required';
        }

        if (validationError) {
          setInternalError(validationError);
          // Don't call formUtils.associateError as we handle it internally
        }
      }

      onBlur?.(e);
    };

    const handleFocus = () => {
      // setIsFocused(true); // Removed as per edit hint
    };

    // Set up required field marking
    useEffect(() => {
      // formUtils.markRequired(inputId); // Removed as per edit hint
    }, [required, inputId]);

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className={cn(
              'block text-sm font-medium mb-1',
              hasError ? 'text-red-700' : 'text-gray-700',
              required && "after:content-['*'] after:ml-0.5 after:text-red-500"
            )}
          >
            {label}
          </label>
        )}

        <div className="relative">
          {leftIcon && (
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <div
                className={cn(
                  'h-4 w-4',
                  hasError ? 'text-red-500' : 'text-gray-400'
                )}
              >
                {leftIcon}
              </div>
            </div>
          )}

          <input
            ref={inputRef}
            id={inputId}
            type={type}
            className={baseClasses}
            aria-invalid={hasError}
            aria-describedby={finalAriaDescribedBy}
            aria-required={required}
            required={required}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            {...props}
          />

          {(rightIcon || loading) && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
              {loading ? (
                <div
                  className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600"
                  aria-label="Loading"
                />
              ) : (
                <div
                  className={cn(
                    'h-4 w-4',
                    hasError ? 'text-red-500' : 'text-gray-400'
                  )}
                >
                  {rightIcon}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Error Message */}
        {currentError && (
          <div
            id={errorId}
            className="mt-1 text-sm text-red-600"
            role="alert"
            aria-live="polite"
          >
            {currentError}
          </div>
        )}

        {/* Helper Text */}
        {helperText && !currentError && (
          <div id={helperId} className="mt-1 text-sm text-gray-500">
            {helperText}
          </div>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
